import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  DocumentMagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { ConfirmationModal } from "./delete-confirmation-modal";
import { FilterOption, FiltersSection } from "./filter-group";
import { Button } from "./button";
import { FilterApplied } from "./filter-applied";

interface ActionButton<T> {
  icon?: React.ReactNode;
  label: string;
  onClick: (row: T) => void;
  className?: string;
}

interface DeleteConfirmationOptions<T> {
  titleField?: keyof T | string;
  titlePrefix?: string;
  message?: string | ((row: T) => string);
  confirmLabel?: string;
  cancelLabel?: string;
}

interface SortConfig<T> {
  sortBy: keyof T | string | null;
  sortDirection: "asc" | "desc" | null;
  onSort: (
    sortBy: keyof T | string | null,
    sortDirection: "asc" | "desc" | null
  ) => void;
}

export interface FilterConfig {
  key: string;
  label: string;
  options: FilterOption[];
  multiple?: boolean;
  type?: "standard" | "custom";
}

interface CrudTableProps<T> {
  data: T[];
  columns: {
    key: keyof T | string;
    label: string;
    render?: (value: any, row: T) => React.ReactNode;
    sortable?: boolean;
    searchable?: boolean;
    width?: number;
    minWidth?: number;
    resizable?: boolean;
    hideOnMobile?: boolean;
  }[];
  onAdd?: {
    onClick: () => void;
    label?: string;
  };
  actions?: {
    view?: ActionButton<T>;
    edit?: ActionButton<T>;
    delete?: ActionButton<T>;
    custom?: ActionButton<T>[];
  };
  deleteConfirmation?: DeleteConfirmationOptions<T>;
  pagination?: {
    page: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    labels?: {
      next?: string;
      previous?: string;
      pageInfo?: string;
    };
  };
  sort?: SortConfig<T>;
  search?: {
    placeholder: string;
    onSearch: (query: string, searchableColumns: (keyof T | string)[]) => void;
    debounceTime?: number;
  };
  filters?: FilterConfig[];
  onFilterChange?: (selectedFilters: Record<string, string[]>) => void;
  totalCount?: number;
  mobileCardTitle?: keyof T | string;
}

const CrudTable = <T extends {}>({
  data,
  columns,
  onAdd,
  actions,
  deleteConfirmation,
  pagination,
  search,
  sort,
  filters,
  onFilterChange,
  totalCount,
  mobileCardTitle,
}: CrudTableProps<T>) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean;
    row: T | null;
  }>({ isOpen: false, row: null });
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >({});
  const [showFilters, setShowFilters] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const tableRef = useRef<HTMLTableElement>(null);

  useEffect(() => {
    const initialWidths: Record<string, number> = {};
    columns.forEach((col) => {
      if (col.width) {
        initialWidths[col.key.toString()] = col.width;
      }
    });
    setColumnWidths(initialWidths);
  }, [columns]);

  const normalizeString = (str: string) =>
    str
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const getNestedValue = (obj: any, path: string | keyof T): any => {
    if (typeof path === "string") {
      return path.split(".").reduce((acc, key) => acc?.[key], obj);
    }
    return obj[path];
  };

  const executeSearch = (query: string) => {
    if (search) {
      const normalizedQuery = normalizeString(query);
      const hasExplicitSearchable = columns.some(
        (col) => col.searchable !== undefined
      );
      const searchableColumns = hasExplicitSearchable
        ? columns.filter((col) => col.searchable).map((col) => col.key)
        : columns.map((col) => col.key);
      search.onSearch(normalizedQuery, searchableColumns);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    const debounceTime = search?.debounceTime || 500;
    searchTimeoutRef.current = setTimeout(() => {
      executeSearch(newValue);
    }, debounceTime);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleSort = (columnKey: keyof T | string) => {
    if (!sort) return;
    const isCurrent = sort.sortBy === columnKey;
    let newSortBy: keyof T | string | null = columnKey;
    let newDirection: "asc" | "desc" | null = "asc";
    if (isCurrent) {
      if (sort.sortDirection === "asc") {
        newDirection = "desc";
      } else {
        newSortBy = null;
        newDirection = null;
      }
    }
    sort.onSort(newSortBy, newDirection);
  };

  useEffect(() => {
    if (onFilterChange) {
      onFilterChange(selectedFilters);
    }
  }, [selectedFilters, onFilterChange]);

  const handleFilterChange = (filterKey: string, value: string) => {
    setSelectedFilters((prev) => {
      const newFilters = { ...prev };
      if (!filters?.find((f) => f.key === filterKey)?.multiple) {
        newFilters[filterKey] = [value];
      } else {
        newFilters[filterKey] = newFilters[filterKey]?.includes(value)
          ? newFilters[filterKey].filter((v) => v !== value)
          : [...(newFilters[filterKey] || []), value];
        if (newFilters[filterKey]?.length === 0) {
          delete newFilters[filterKey];
        }
      }
      return newFilters;
    });
  };

  const handleDelete = (row: T) => {
    setDeleteModalState({ isOpen: true, row });
  };

  const handleConfirmDelete = () => {
    if (deleteModalState.row && actions?.delete) {
      actions.delete.onClick(deleteModalState.row);
      setDeleteModalState({ isOpen: false, row: null });
    }
  };

  const getDeleteMessage = (row: T): string => {
    if (!deleteConfirmation)
      return "¿Estás seguro de que deseas eliminar este elemento?";
    if (typeof deleteConfirmation.message === "function") {
      return deleteConfirmation.message(row);
    }
    if (deleteConfirmation.message) return deleteConfirmation.message;
    if (deleteConfirmation.titleField) {
      const prefix =
        deleteConfirmation.titlePrefix ||
        "¿Estás seguro de que deseas eliminar a";
      const titleValue = getNestedValue(row, deleteConfirmation.titleField);
      return `${prefix} ${titleValue}?`;
    }
    return "¿Estás seguro de que deseas eliminar este elemento?";
  };

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, columnKey: string) => {
      e.preventDefault();
      setResizingColumn(columnKey);
      const startX = e.pageX;
      const column = columns.find((col) => col.key.toString() === columnKey);
      const startWidth = columnWidths[columnKey] || column?.width || 150;
      const handleMouseMove = (e: MouseEvent) => {
        const diff = e.pageX - startX;
        const newWidth = Math.max(column?.minWidth || 80, startWidth + diff);
        setColumnWidths((prev) => ({
          ...prev,
          [columnKey]: newWidth,
        }));
      };
      const handleMouseUp = () => {
        setResizingColumn(null);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [columns, columnWidths]
  );

  const hasActions =
    actions &&
    (actions.view || actions.edit || actions.delete || actions.custom?.length);

  const removeFilter = (filterKey: string, value: string | number): void => {
    setSelectedFilters((prev) => {
      const newFilters = { ...prev };
      newFilters[filterKey] =
        newFilters[filterKey]?.filter((v) => v !== value) || [];
      if (newFilters[filterKey].length === 0) {
        delete newFilters[filterKey];
      }
      return newFilters;
    });
  };

  const getColumnStyle = (column: any) => {
    const key = column.key.toString();
    const width = columnWidths[key] || column.width;
    return width
      ? { width: `${width}px`, minWidth: `${column.minWidth || 80}px` }
      : {};
  };

  const MobileCard = ({ row, index }: { row: T; index: number }) => {
    const titleField = mobileCardTitle || columns[0]?.key;
    const title = getNestedValue(row, titleField);

    return (
      <div
        className={`${
          index % 2 === 0 ? "bg-white" : "bg-gray-50"
        } border border-gray-200 rounded-lg p-4 mb-3 shadow-sm`}
      >
        {/* Card Title */}
        {title && (
          <div className="font-semibold text-gray-900 mb-3 text-lg border-b border-gray-200 pb-2">
            {title}
          </div>
        )}

        {/* Card Content */}
        <div className="space-y-2">
          {columns
            .filter((col) => col.key !== "description" && !col.hideOnMobile)
            .slice(mobileCardTitle ? 1 : 0)
            .map((col) => {
              const value = getNestedValue(row, col.key as string);
              const renderedValue = col.render
                ? col.render(value, row)
                : value?.toString();

              if (!value && value !== 0) return null;

              return (
                <div
                  key={col.key.toString()}
                  className="flex justify-between items-start"
                >
                  <span className="text-sm font-medium text-gray-600 min-w-0 flex-shrink-0 mr-3">
                    {col.label}:
                  </span>
                  <span className="text-sm text-gray-900 text-right min-w-0">
                    {renderedValue}
                  </span>
                </div>
              );
            })}
        </div>

        {/* Actions */}
        {hasActions && (
          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="flex flex-wrap gap-2">
              {actions.view && (
                <button
                  onClick={() => actions.view!.onClick(row)}
                  className={
                    actions.view.className ||
                    "flex items-center px-3 py-2 bg-blue-500 text-white font-semibold rounded-md hover:bg-blue-600 transition text-sm flex-1 justify-center"
                  }
                >
                  {actions.view.icon || <EyeIcon className="w-4 h-4 mr-1" />}
                  {actions.view.label}
                </button>
              )}
              {actions.edit && (
                <button
                  onClick={() => actions.edit!.onClick(row)}
                  className={
                    actions.edit.className ||
                    "flex items-center px-3 py-2 bg-yellow-500 text-white font-semibold rounded-md hover:bg-yellow-600 transition text-sm flex-1 justify-center"
                  }
                >
                  {actions.edit.icon || <PencilIcon className="w-4 h-4 mr-1" />}
                  {actions.edit.label}
                </button>
              )}
              {actions.delete && (
                <button
                  onClick={() => handleDelete(row)}
                  className={
                    actions.delete.className ||
                    "flex items-center px-3 py-2 bg-red-500 text-white font-semibold rounded-md hover:bg-red-600 transition text-sm flex-1 justify-center"
                  }
                >
                  {actions.delete.icon || (
                    <TrashIcon className="w-4 h-4 mr-1" />
                  )}
                  {actions.delete.label}
                </button>
              )}
              {actions.custom?.map((action, i) => (
                <button
                  key={i}
                  onClick={() => action.onClick(row)}
                  className={
                    action.className ||
                    "flex items-center px-3 py-2 bg-gray-500 text-white font-semibold rounded-md hover:bg-gray-600 transition text-sm flex-1 justify-center"
                  }
                >
                  {action.icon}
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Search and Filters Row */}
      <div className="flex flex-col sm:flex-row items-center w-full mb-4 gap-2">
        {search && (
          <div className="flex-1 flex items-center border border-gray-300 rounded-lg bg-gray-50 w-full sm:w-auto">
            <div className="flex items-center pl-3">
              <MagnifyingGlassIcon className="w-4 h-4 text-gray-500 ml-2" />
            </div>
            <input
              type="text"
              placeholder={search.placeholder}
              value={searchQuery}
              onChange={handleSearch}
              className="w-full py-2 px-2 bg-transparent border-none focus:ring-0 focus:outline-none text-sm"
            />
          </div>
        )}
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {filters && filters.length > 0 && (
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex-shrink-0 flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 w-full sm:w-auto justify-center"
            >
              <FunnelIcon className="w-4 h-4 mr-2" />
              Filtros{" "}
              {showFilters ? (
                <ChevronUpIcon className="w-4 h-4 ml-1" />
              ) : (
                <ChevronDownIcon className="w-4 h-4 ml-1" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Create Button */}
      {onAdd && (
        <div className="flex justify-end mb-4">
          <Button
            onClick={onAdd.onClick}
            text={onAdd.label || "Agregar"}
            variant="create"
            className="w-full sm:w-auto"
          />
        </div>
      )}

      {filters && filters.length > 0 && showFilters && (
        <FiltersSection
          filters={filters}
          showFilters={showFilters}
          selectedFilters={selectedFilters}
          handleFilterChange={handleFilterChange}
        />
      )}

      {filters && filters.length > 0 && (
        <FilterApplied
          selectedFilters={selectedFilters}
          onClearFilters={() => {
            setSelectedFilters({});
          }}
          onRemoveFilter={removeFilter}
          filters={filters}
        />
      )}

      {/* Mobile Sort Control */}
      {sort && (
        <div className="block md:hidden mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Ordenar por:
          </label>
          <select
            value={
              sort.sortBy ? `${String(sort.sortBy)}-${sort.sortDirection}` : ""
            }
            onChange={(e) => {
              const value = e.target.value;
              if (!value) {
                sort.onSort(null, null);
              } else {
                const [sortBy, direction] = value.split("-");
                sort.onSort(sortBy, direction as "asc" | "desc");
              }
            }}
            className="w-full px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Sin ordenar</option>
            {columns
              .filter((col) => col.sortable)
              .map((col) => (
                <React.Fragment key={col.key.toString()}>
                  <option value={`${String(col.key)}-asc`}>
                    {col.label} (A-Z)
                  </option>
                  <option value={`${String(col.key)}-desc`}>
                    {col.label} (Z-A)
                  </option>
                </React.Fragment>
              ))}
          </select>
        </div>
      )}

      {totalCount !== undefined && totalCount > 0 && (
        <div className="flex justify-end items-center mb-3 text-sm text-gray-600">
          <DocumentMagnifyingGlassIcon className="h-5 w-5 mr-1 text-gray-500" />
          <span className="font-medium mr-1">{totalCount}</span>{" "}
          {totalCount === 1 ? "resultado" : "resultados"}
        </div>
      )}

      {totalCount === 0 && (
        <div className="flex justify-end items-center mb-3 text-sm text-gray-500 italic">
          <DocumentMagnifyingGlassIcon className="h-5 w-5 mr-1 text-gray-400" />
          No se encontraron resultados
        </div>
      )}

      {data.length === 0 ? (
        <div className="text-center text-gray-600 py-4">
          No hay registros para mostrar en este momento.
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table
              ref={tableRef}
              className="w-full border-collapse"
              style={{ tableLayout: "fixed" }}
            >
              <thead>
                <tr className="bg-gray-100">
                  {columns
                    .filter((col) => col.key !== "description")
                    .map((col) => (
                      <th
                        key={col.key.toString()}
                        className="border p-3 text-left text-sm font-semibold text-gray-700 uppercase hover:bg-gray-200 relative"
                        style={getColumnStyle(col)}
                      >
                        <div
                          className="flex items-center justify-between cursor-pointer"
                          onClick={() => col.sortable && handleSort(col.key)}
                        >
                          <span className="truncate pr-2">{col.label}</span>
                          {sort && col.sortable && (
                            <span className="ml-2 flex-shrink-0">
                              {sort.sortBy === col.key ? (
                                sort.sortDirection === "asc" ? (
                                  "↑"
                                ) : sort.sortDirection === "desc" ? (
                                  "↓"
                                ) : (
                                  <span className="opacity-50">↕</span>
                                )
                              ) : (
                                <span className="opacity-50">↕</span>
                              )}
                            </span>
                          )}
                        </div>
                        {col.resizable !== false && (
                          <div
                            className="absolute top-0 right-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-400 transition-colors"
                            onMouseDown={(e) =>
                              handleMouseDown(e, col.key.toString())
                            }
                            style={{
                              backgroundColor:
                                resizingColumn === col.key.toString()
                                  ? "#3b82f6"
                                  : "transparent",
                            }}
                          />
                        )}
                      </th>
                    ))}
                  {hasActions && (
                    <th className="border p-3 text-left text-sm font-semibold text-gray-700 uppercase">
                      Acciones
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {data.map((row, index) => (
                  <tr
                    key={index}
                    className={`${
                      index % 2 === 0 ? "bg-white" : "bg-gray-50"
                    } hover:bg-gray-100 transition`}
                  >
                    {columns
                      .filter((col) => col.key !== "description")
                      .map((col) => (
                        <td
                          key={col.key.toString()}
                          className="border p-3 text-sm text-gray-700 overflow-hidden"
                          style={getColumnStyle(col)}
                        >
                          <div
                            className="truncate"
                            title={getNestedValue(
                              row,
                              col.key as string
                            )?.toString()}
                          >
                            {col.render
                              ? col.render(
                                  getNestedValue(row, col.key as string),
                                  row
                                )
                              : getNestedValue(
                                  row,
                                  col.key as string
                                )?.toString()}
                          </div>
                        </td>
                      ))}
                    {hasActions && (
                      <td className="border p-3">
                        <div className="flex items-center space-x-2">
                          {actions.view && (
                            <button
                              onClick={() => actions.view!.onClick(row)}
                              className={
                                actions.view.className ||
                                "flex items-center px-2 py-1 bg-blue-500 text-white font-semibold rounded-md hover:bg-blue-600 transition text-xs"
                              }
                            >
                              {actions.view.icon || (
                                <EyeIcon className="w-4 h-4" />
                              )}
                              {actions.view.label}
                            </button>
                          )}
                          {actions.edit && (
                            <button
                              onClick={() => actions.edit!.onClick(row)}
                              className={
                                actions.edit.className ||
                                "flex items-center px-2 py-1 bg-yellow-500 text-white font-semibold rounded-md hover:bg-yellow-600 transition text-xs"
                              }
                            >
                              {actions.edit.icon || (
                                <PencilIcon className="w-4 h-4" />
                              )}
                              {actions.edit.label}
                            </button>
                          )}
                          {actions.delete && (
                            <button
                              onClick={() => handleDelete(row)}
                              className={
                                actions.delete.className ||
                                "flex items-center px-2 py-1 bg-red-500 text-white font-semibold rounded-md hover:bg-red-600 transition text-xs"
                              }
                            >
                              {actions.delete.icon || (
                                <TrashIcon className="w-4 h-4" />
                              )}
                              {actions.delete.label}
                            </button>
                          )}
                          {actions.custom?.map((action, i) => (
                            <button
                              key={i}
                              onClick={() => action.onClick(row)}
                              className={
                                action.className ||
                                "flex items-center px-2 py-1 bg-gray-500 text-white font-semibold rounded-md hover:bg-gray-600 transition text-xs"
                              }
                            >
                              {action.icon}
                              {action.label}
                            </button>
                          ))}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="block md:hidden">
            {data.map((row, index) => (
              <MobileCard key={index} row={row} index={index} />
            ))}
          </div>
        </>
      )}

      {pagination && (
        <div className="mt-4 flex flex-col sm:flex-row justify-between items-center gap-2">
          <button
            onClick={() => pagination.onPageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
            className="flex items-center px-4 py-2 bg-gray-200 text-gray-800 font-semibold rounded-md hover:bg-gray-300 transition text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeftIcon className="w-4 h-4 mr-1" />
            {pagination.labels?.previous || "Anterior"}
          </button>
          {pagination.totalPages > 0 && (
            <span className="text-gray-800 text-xs sm:text-sm">
              {pagination.labels?.pageInfo ||
                `Página ${pagination.page} de ${pagination.totalPages}`}
            </span>
          )}
          <button
            onClick={() => pagination.onPageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
            className="flex items-center px-4 py-2 bg-gray-200 text-gray-800 font-semibold rounded-md hover:bg-gray-300 transition text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {pagination.labels?.next || "Siguiente"}
            <ChevronRightIcon className="w-4 h-4 ml-1" />
          </button>
        </div>
      )}

      <ConfirmationModal
        isOpen={deleteModalState.isOpen}
        onClose={() => setDeleteModalState({ isOpen: false, row: null })}
        onConfirm={handleConfirmDelete}
        title={deleteConfirmation?.titlePrefix || "Confirmar eliminación"}
        message={
          deleteModalState.row ? getDeleteMessage(deleteModalState.row) : ""
        }
        confirmLabel={deleteConfirmation?.confirmLabel || "Eliminar"}
        cancelLabel={deleteConfirmation?.cancelLabel || "Cancelar"}
      />
    </div>
  );
};

export default CrudTable;
